import { teamsService } from "@/services/teamsService";
import { toast } from "sonner";

export interface TeamWellnessSettings {
  id: string;
  team_id: string;
  break_reminders_enabled: boolean;
  meditation_schedule: {
    enabled: boolean;
    times: string[];
  };
  wellness_resources_accessed: string[];
  created_at: string;
  updated_at: string;
}

export interface WellnessReminder {
  type: "break" | "meditation" | "check-in";
  time: string;
  message: string;
  enabled: boolean;
}

/**
 * Fetch team wellness settings
 */
export const fetchTeamWellnessSettings = async (
  teamId: string
): Promise<TeamWellnessSettings | null> => {
  try {
    const response = await teamsService.getSettings(teamId);

    if (!response.success) {
      console.error("Error fetching wellness settings:", response.error);
      return null;
    }

    return response.data;
  } catch (error) {
    console.error("Failed to fetch wellness settings:", error);
    return null;
  }
};

/**
 * Toggle break reminders
 */
export const toggleBreakReminders = async (
  teamId: string,
  enabled: boolean
): Promise<boolean> => {
  try {
    const response = await teamsService.updateSettings(teamId, {
      break_reminders_enabled: enabled,
    });

    if (!response.success) {
      console.error("Error toggling break reminders:", response.error);
      toast.error("Failed to update break reminders");
      return false;
    }

    toast.success(
      enabled 
        ? "Break reminders enabled for your team" 
        : "Break reminders disabled"
    );
    return true;
  } catch (error) {
    console.error("Failed to toggle break reminders:", error);
    toast.error("Failed to update break reminders");
    return false;
  }
};

/**
 * Update meditation schedule
 */
export const updateMeditationSchedule = async (
  teamId: string,
  schedule: { enabled: boolean; times: string[] }
): Promise<boolean> => {
  try {
    const response = await teamsService.updateSettings(teamId, {
      meditation_schedule: schedule,
    });

    if (!response.success) {
      console.error("Error updating meditation schedule:", response.error);
      toast.error("Failed to update meditation schedule");
      return false;
    }

    toast.success("Meditation schedule updated successfully");
    return true;
  } catch (error) {
    console.error("Failed to update meditation schedule:", error);
    toast.error("Failed to update meditation schedule");
    return false;
  }
};

/**
 * Track wellness resource access
 */
export const trackWellnessResourceAccess = async (
  teamId: string,
  resourceId: string
): Promise<boolean> => {
  try {
    // Get current settings first
    const settings = await fetchTeamWellnessSettings(teamId);
    if (!settings) return false;

    const accessed = settings.wellness_resources_accessed || [];
    if (!accessed.includes(resourceId)) {
      accessed.push(resourceId);
    }

    const response = await teamsService.updateSettings(teamId, {
      wellness_resources_accessed: accessed,
    });

    if (!response.success) {
      console.error("Error tracking resource access:", response.error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Failed to track resource access:", error);
    return false;
  }
};

/**
 * Get team wellness insights
 */
export const getTeamWellnessInsights = async (
  teamId: string
): Promise<{
  averageCheckInRate: number;
  wellnessResourcesUsed: number;
  breakRemindersActive: boolean;
  recentStressLevel: number;
} | null> => {
  try {
    const response = await teamsService.getStats(teamId, {
      type: 'wellness-insights',
    });

    if (!response.success) {
      console.error("Failed to get wellness insights:", response.error);
      return null;
    }

    return response.data;
  } catch (error) {
    console.error("Failed to get wellness insights:", error);
    return null;
  }
};

/**
 * Schedule a team wellness activity
 */
export const scheduleWellnessActivity = async (
  teamId: string,
  activity: {
    type: "meditation" | "stretch" | "break" | "team-building";
    scheduledFor: Date;
    duration: number; // in minutes
    description?: string;
  }
): Promise<boolean> => {
  try {
    const response = await teamsService.createEvent(teamId, {
      title: `Team ${activity.type} session`,
      description: activity.description || `${activity.duration}-minute ${activity.type} session`,
      event_date: activity.scheduledFor.toISOString(),
      event_type: "wellness",
      duration: activity.duration,
    });

    if (!response.success) {
      console.error("Error scheduling wellness activity:", response.error);
      toast.error("Failed to schedule activity");
      return false;
    }

    toast.success("Wellness activity scheduled successfully!");
    return true;
  } catch (error) {
    console.error("Failed to schedule wellness activity:", error);
    toast.error("Failed to schedule activity");
    return false;
  }
};
